import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import { useApi } from '~/contexts';
import { colors } from '~/styles';
import {
  Grid,
  Card,
  CardLabel,
  CardValue,
  CardSubValue,
  Section,
  SectionTitle,
  ChartLabel,
  ChartWrapper,
} from './shared';

function ConversationDepth({ start, end }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getConversationDepth({ start, end });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end]);

  if (isLoading || !data) return null;

  return (
    <>
      <Section>
        <SectionTitle>Conversations &amp; Messages</SectionTitle>
        <Grid>
          <Card>
            <CardLabel>Total Conversations</CardLabel>
            <CardValue>{data.totalConversations.toLocaleString()}</CardValue>
            <CardSubValue>In the selected time period</CardSubValue>
          </Card>
          <Card>
            <CardLabel>Avg Turns per Conversation</CardLabel>
            <CardValue>{data.avgTurns.toFixed(1)}</CardValue>
            <CardSubValue>User messages per conversation</CardSubValue>
          </Card>
          <Card>
            <CardLabel>Max Turns</CardLabel>
            <CardValue>{data.maxTurns}</CardValue>
            <CardSubValue>Deepest conversation in the period</CardSubValue>
          </Card>
        </Grid>
      </Section>
      <TurnDistribution data={data} />
    </>
  );
}

function TurnDistribution({ data }) {
  const chartData = useMemo(() => {
    if (!data?.distribution || data.distribution.every((d) => d.count === 0)) return null;
    return {
      labels: data.distribution.map((d) => d.label),
      datasets: [
        {
          data: data.distribution.map((d) => d.count),
          backgroundColor: colors.primary,
        },
      ],
    };
  }, [data]);

  if (!chartData) return null;

  return (
    <Section>
      <ChartLabel>Turn Distribution</ChartLabel>
      <ChartWrapper>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: { y: { ticks: { precision: 0 } } },
          }}
        />
      </ChartWrapper>
    </Section>
  );
}

function ActivityOverTime({ start, end, granularity = 'day' }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getActivityOverTime({ start, end, granularity });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end, granularity]);

  const formatMap = { day: 'MMM D', week: 'MMM D', month: 'MMM YYYY' };
  const dateFormat = formatMap[granularity] || 'MMM D';
  const labelMap = { day: 'Daily', week: 'Weekly', month: 'Monthly' };
  const granularityLabel = labelMap[granularity] || 'Daily';

  const conversationsChart = useMemo(() => {
    if (!data || data.every((d) => d.conversations === 0)) return null;
    return {
      labels: data.map((d) => moment.utc(d.date).format(dateFormat)),
      datasets: [
        {
          label: 'Conversations',
          data: data.map((d) => d.conversations),
          backgroundColor: colors.primary,
        },
      ],
    };
  }, [data, dateFormat]);

  const messagesChart = useMemo(() => {
    if (!data || data.every((d) => d.userMessages === 0)) return null;
    return {
      labels: data.map((d) => moment.utc(d.date).format(dateFormat)),
      datasets: [
        {
          label: 'User Messages',
          data: data.map((d) => d.userMessages),
          backgroundColor: colors.primary,
        },
      ],
    };
  }, [data, dateFormat]);

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { ticks: { precision: 0 }, beginAtZero: true } },
  };

  if (isLoading || !data) return null;

  return (
    <>
      {conversationsChart && (
        <Section>
          <ChartLabel>Conversations ({granularityLabel})</ChartLabel>
          <ChartWrapper>
            <Bar data={conversationsChart} options={barOptions} />
          </ChartWrapper>
        </Section>
      )}
      {messagesChart && (
        <Section>
          <ChartLabel>User Messages ({granularityLabel})</ChartLabel>
          <ChartWrapper>
            <Bar data={messagesChart} options={barOptions} />
          </ChartWrapper>
        </Section>
      )}
    </>
  );
}

function MissedMessages({ start, end, granularity = 'day' }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getMissedMessages({ start, end, granularity });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end, granularity]);

  const formatMap = { day: 'MMM D', week: 'MMM D', month: 'MMM YYYY' };
  const dateFormat = formatMap[granularity] || 'MMM D';
  const labelMap = { day: 'Daily', week: 'Weekly', month: 'Monthly' };
  const granularityLabel = labelMap[granularity] || 'Daily';

  const chartData = useMemo(() => {
    if (!data || data.every((d) => d.missedMessages === 0)) return null;
    return {
      labels: data.map((d) => moment.utc(d.date).format(dateFormat)),
      datasets: [
        {
          label: 'Missed Messages',
          data: data.map((d) => d.missedMessages),
          backgroundColor: colors.danger,
        },
      ],
    };
  }, [data, dateFormat]);

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { ticks: { precision: 0 }, beginAtZero: true } },
  };

  if (isLoading || !data || !chartData) return null;

  return (
    <Section>
      <ChartLabel>Missed Messages ({granularityLabel})</ChartLabel>
      <ChartWrapper>
        <Bar data={chartData} options={barOptions} />
      </ChartWrapper>
    </Section>
  );
}

function FeedbackOverTime({ start, end, granularity = 'day' }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getFeedbackOverTime({ start, end, granularity });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end, granularity]);

  const formatMap = { day: 'MMM D', week: 'MMM D', month: 'MMM YYYY' };
  const dateFormat = formatMap[granularity] || 'MMM D';
  const labelMap = { day: 'Daily', week: 'Weekly', month: 'Monthly' };
  const granularityLabel = labelMap[granularity] || 'Daily';

  const chartData = useMemo(() => {
    if (!data || data.every((d) => d.thumbsUp === 0 && d.thumbsDown === 0)) return null;
    return {
      labels: data.map((d) => moment.utc(d.date).format(dateFormat)),
      datasets: [
        {
          label: 'Thumbs Up',
          data: data.map((d) => d.thumbsUp),
          backgroundColor: colors.primary,
        },
        {
          label: 'Thumbs Down',
          data: data.map((d) => d.thumbsDown),
          backgroundColor: colors.danger,
        },
      ],
    };
  }, [data, dateFormat]);

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' },
    plugins: {
      legend: { display: true },
      tooltip: {
        callbacks: {
          footer: (items) => {
            const total = items.reduce((sum, item) => sum + item.raw, 0);
            return `Total: ${total}`;
          },
        },
      },
    },
    scales: { x: { stacked: true }, y: { stacked: true, ticks: { precision: 0 }, beginAtZero: true } },
  };

  if (isLoading || !data || !chartData) return null;

  return (
    <Section>
      <ChartLabel>Feedback ({granularityLabel})</ChartLabel>
      <ChartWrapper>
        <Bar data={chartData} options={barOptions} />
      </ChartWrapper>
    </Section>
  );
}

function Feedback({ start, end, granularity }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getFeedback({ start, end });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end]);

  if (isLoading || !data) return null;

  return (
    <>
      <Section>
        <SectionTitle>Feedback</SectionTitle>
        <Grid>
          <Card>
            <CardLabel>Positive Rate</CardLabel>
            <CardValue>{(data.positiveRate * 100).toFixed(0)}%</CardValue>
            <CardSubValue>
              <strong>{data.thumbsUp}</strong> thumbs up of <strong>{data.total}</strong> rated responses
            </CardSubValue>
          </Card>
          <Card>
            <CardLabel>Thumbs Up</CardLabel>
            <CardValue>{data.thumbsUp}</CardValue>
          </Card>
          <Card>
            <CardLabel>Thumbs Down</CardLabel>
            <CardValue>{data.thumbsDown}</CardValue>
          </Card>
        </Grid>
      </Section>
      <FeedbackOverTime start={start} end={end} granularity={granularity} />
    </>
  );
}

export default function Conversations({ start, end, granularity }) {
  return (
    <>
      <ConversationDepth start={start} end={end} />
      <ActivityOverTime start={start} end={end} granularity={granularity} />
      <MissedMessages start={start} end={end} granularity={granularity} />
      <Feedback start={start} end={end} granularity={granularity} />
    </>
  );
}
